import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Chess } from 'chess.js';
import ChessBoard from './ChessBoard';
import Icon from '../Shared/Icons';
import { getLifeLesson } from '../../utils/chessLifeLessons';
import { analyzePosition } from '../../utils/chessBot';

const START_FEN = new Chess().fen();

// ── Move classification ─────────────────────────────────────────────────
// "loss" = how many centipawns of value the played move gave away compared
// to the engine's best move in that position (pawn ≈ 100).
const CLASSES = {
  best:       { key: 'best',       label: 'Move Bora',   emoji: '★', color: '#34D399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.4)' },
  good:       { key: 'good',       label: 'Nzuri',       emoji: '✓', color: '#3FBE83', bg: 'rgba(63,190,131,0.08)', border: 'rgba(63,190,131,0.35)' },
  okay:       { key: 'okay',       label: 'Sawa',        emoji: '·', color: 'rgba(236,239,234,0.58)', bg: 'rgba(160,174,166,0.08)', border: 'rgba(160,174,166,0.3)' },
  inaccuracy: { key: 'inaccuracy', label: 'Dosari',      emoji: '?!', color: '#34D399', bg: 'rgba(251,191,36,0.09)', border: 'rgba(251,191,36,0.4)' },
  mistake:    { key: 'mistake',    label: 'Kosa',        emoji: '?', color: '#FB923C', bg: 'rgba(251,146,60,0.08)', border: 'rgba(251,146,60,0.4)' },
  blunder:    { key: 'blunder',    label: 'Kosa Kubwa',  emoji: '??', color: '#F87171', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.45)' },
};

function classify(loss, isEngineBest) {
  if (isEngineBest || loss <= 15) return CLASSES.best;
  if (loss <= 45) return CLASSES.good;
  if (loss <= 100) return CLASSES.okay;
  if (loss <= 250) return CLASSES.inaccuracy;
  if (loss <= 500) return CLASSES.mistake;
  return CLASSES.blunder;
}

function pawns(cp) {
  return (Math.round(Math.abs(cp) / 10) / 10).toFixed(1);
}

// Swahili explanation for each analyzed move — WHY it was good or bad,
// plus the better alternative when one existed.
function buildReason(entry) {
  const { cls, san, loss, best } = entry;
  const isCapture = san.includes('x');
  const isCheck = san.includes('+') || san.includes('#');
  const isMate = san.includes('#');
  const isCastle = san.startsWith('O-O');
  const isPromo = san.includes('=');

  if (isMate) {
    return 'Checkmate! Umeumaliza mchezo kwa usahihi kabisa.';
  }

  if (cls.key === 'best') {
    if (isCapture) return 'Chaguo bora kabisa — umechukua kipande kwa wakati sahihi, kama injini ingecheza.';
    if (isCheck) return 'Move bora — check hii inamlazimisha mpinzani kujibu, ukibaki na mpango wako.';
    if (isCastle) return 'Move bora — castling imemweka mfalme wako salama na kuunganisha rook zako.';
    if (isPromo) return 'Bora kabisa — promotion hii inabadilisha mchezo kwa faida yako.';
    return 'Hii ndiyo move injini ingecheza — umeshika mpango sahihi kabisa.';
  }
  if (cls.key === 'good') {
    return `Move nzuri sana — inakaribia chaguo bora (${best.san}). Umeendeleza mpango wako bila kupoteza kitu.`;
  }
  if (cls.key === 'okay') {
    return `Move salama, lakini kulikuwa na chaguo kali zaidi: ${best.san} (${best.from}→${best.to}) ingekupa msukumo zaidi.`;
  }
  if (cls.key === 'inaccuracy') {
    return `Dosari ndogo — umepoteza takriban pointi ${pawns(loss)}. Bora ungecheza ${best.san} (${best.from}→${best.to}) kulinda faida yako.`;
  }
  if (cls.key === 'mistake') {
    return `Kosa — move hii imempa mpinzani nafasi, ukipoteza takriban pointi ${pawns(loss)}. Chaguo bora lilikuwa ${best.san} (${best.from}→${best.to}).`;
  }
  return `Kosa kubwa! Umepoteza takriban pointi ${pawns(loss)} — mara nyingi hii ni kuacha kipande bila ulinzi au kupuuza tishio. Ungecheza ${best.san} (${best.from}→${best.to}) ungebaki salama.`;
}

export default function ChessReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [moveIndex, setMoveIndex] = useState(0); // 0 = starting position — review begins from move one
  const [analysis, setAnalysis] = useState([]); // one entry per move, filled progressively
  const [analyzing, setAnalyzing] = useState(false);
  const cancelledRef = useRef(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved

  async function handleSaveHighlight() {
    if (!game || saveState !== 'idle') return;
    setSaveState('saving');
    try {
      await updateDoc(doc(db, 'chessGames', id), {
        isSaved: true,
        isPrivate: false,
        startFen: new Chess().fen(),
        savedAt: serverTimestamp(),
      });
      setSaveState('saved');
    } catch (e) { console.error(e); setSaveState('idle'); }
  }

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'chessGames', id));
        if (!snap.exists()) { setNotFound(true); return; }
        const data = { id: snap.id, ...snap.data() };
        setGame(data);
        setMoveIndex(0); // start the story at the very beginning
      } catch (e) {
        console.error(e);
        setNotFound(true);
      }
    })();
  }, [id]);

  // ── Progressive engine analysis ───────────────────────────────────────
  // Analyzes one move per tick so the phone stays responsive; results
  // appear as they're computed.
  useEffect(() => {
    if (!game) return;
    const history = game.moveHistory || [];
    if (history.length === 0) return;
    cancelledRef.current = false;
    setAnalyzing(true);
    setAnalysis([]);

    const results = [];
    let i = 0;

    function step() {
      if (cancelledRef.current) return;
      if (i >= history.length) {
        setAnalyzing(false);
        return;
      }
      const beforeFen = i === 0 ? START_FEN : history[i - 1].fen;
      const move = history[i];
      let entry = null;
      try {
        const pos = analyzePosition(beforeFen, 2);
        if (pos && pos.best) {
          const mover = pos.turn; // side that made this move
          const playedScore = pos.scores[move.san];
          if (playedScore != null) {
            // Positive loss = value given away, from the mover's own side.
            const raw = (pos.best.score - playedScore) * (mover === 'w' ? 1 : -1);
            const loss = Math.max(0, raw);
            const isEngineBest = pos.best.san === move.san;
            const cls = classify(loss, isEngineBest);
            entry = { index: i, san: move.san, mover, loss, best: pos.best, cls };
            entry.reason = buildReason(entry);
          }
        }
      } catch (e) { console.error(e); }
      if (!entry) {
        entry = { index: i, san: move.san, mover: move.color || (i % 2 === 0 ? 'w' : 'b'), loss: 0, best: null, cls: CLASSES.okay, reason: 'Move hii haikuweza kuchambuliwa.' };
      }
      results.push(entry);
      setAnalysis([...results]);
      i += 1;
      setTimeout(step, 0); // yield to the UI between moves
    }
    setTimeout(step, 50);

    return () => { cancelledRef.current = true; };
  }, [game?.id, game?.moveHistory?.length]);

  if (notFound) {
    return (
      <div style={styles.page}>
        <div style={styles.centerMsg}>
          Mchezo huu haupatikani. <Link to="/chess" style={styles.link}>Rudi Lobby</Link>
        </div>
      </div>
    );
  }
  if (!game) {
    return <div style={styles.page}><div style={styles.centerMsg}>Inapakia...</div></div>;
  }

  const history = game.moveHistory || [];
  const currentFen = moveIndex === 0 ? START_FEN : history[moveIndex - 1]?.fen || game.fen;
  const currentMove = moveIndex > 0 ? history[moveIndex - 1] : null;
  const currentAnalysis = moveIndex > 0 ? analysis[moveIndex - 1] : null;
  const lesson = game.status === 'finished'
    ? getLifeLesson(game.winner, null, game.resultReason)
    : null;

  // ── Per-player summary (shown once analysis is complete) ─────────────
  function summarize(color) {
    const mine = analysis.filter(a => a.mover === color);
    if (mine.length === 0) return null;
    const counts = { best: 0, good: 0, okay: 0, inaccuracy: 0, mistake: 0, blunder: 0 };
    let totalLoss = 0;
    mine.forEach(a => { counts[a.cls.key] += 1; totalLoss += a.loss; });
    const avgLoss = totalLoss / mine.length;
    // Simple, friendly accuracy: 100% when average loss is 0, sliding down.
    const accuracy = Math.max(35, Math.min(100, Math.round(100 - avgLoss / 6)));
    return { counts, accuracy, moves: mine.length };
  }
  const done = !analyzing && analysis.length === history.length && history.length > 0;
  const whiteSummary = done ? summarize('w') : null;
  const blackSummary = done ? summarize('b') : null;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button style={styles.backIconBtn} onClick={() => navigate('/chess/profile')}>
          <Icon name="door" size={20} color="#ECEFEA" strokeWidth={2.4} />
        </button>
        <div style={styles.gameTitle}>
          {game.white?.name || 'White'} vs {game.black?.name || 'Black'}
        </div>
        <button
          style={{ ...styles.saveIconBtn, ...(saveState !== 'idle' ? styles.saveIconBtnDone : {}) }}
          disabled={saveState !== 'idle'}
          onClick={handleSaveHighlight}
          title="Hifadhi mchezo huu"
        >
          <Icon name={saveState === 'saved' ? 'check' : 'heart'} size={17}
            color={saveState === 'saved' ? '#06281A' : '#34D399'}
            fill={saveState === 'saved' ? '#06281A' : 'none'} strokeWidth={2.2} />
        </button>
      </div>

      <ChessBoard
        fen={currentFen}
        interactive={false}
        lastMove={currentMove ? { from: currentMove.from, to: currentMove.to } : null}
      />

      <div style={styles.controls}>
        <button style={styles.ctrlBtn} onClick={() => setMoveIndex(0)} disabled={moveIndex === 0}>⏮</button>
        <button style={styles.ctrlBtn} onClick={() => setMoveIndex(m => Math.max(0, m - 1))} disabled={moveIndex === 0}>◀ Nyuma</button>
        <div style={styles.moveCounter}>{moveIndex} / {history.length}</div>
        <button style={styles.ctrlBtn} onClick={() => setMoveIndex(m => Math.min(history.length, m + 1))} disabled={moveIndex === history.length}>Mbele ▶</button>
        <button style={styles.ctrlBtn} onClick={() => setMoveIndex(history.length)} disabled={moveIndex === history.length}>⏭</button>
      </div>

      {analyzing && (
        <div style={styles.analyzingBar}>
          Inachambua mchezo… {analysis.length}/{history.length}
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${history.length ? (analysis.length / history.length) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {/* CURRENT MOVE ANALYSIS — why it was good or bad + best alternative */}
      {moveIndex === 0 ? (
        <div style={styles.introCard}>
          <div style={styles.introTitle}>Mwanzo wa Mchezo</div>
          <div style={styles.introText}>
            Bofya <strong>Mbele ▶</strong> kufuata mchezo move kwa move. Kila move
            utaona kama ilikuwa nzuri au kosa, kwa nini, na move bora ambayo
            ingeleta faida zaidi.
          </div>
        </div>
      ) : currentAnalysis ? (
        <div style={{ ...styles.analysisCard, background: currentAnalysis.cls.bg, borderColor: currentAnalysis.cls.border }}>
          <div style={styles.analysisHead}>
            <span style={{ ...styles.classBadge, color: currentAnalysis.cls.color, borderColor: currentAnalysis.cls.border }}>
              {currentAnalysis.cls.emoji} {currentAnalysis.cls.label}
            </span>
            <span style={styles.analysisMove}>
              {Math.floor((moveIndex - 1) / 2) + 1}{currentAnalysis.mover === 'w' ? '.' : '…'} {currentAnalysis.san}
              <span style={styles.moverTag}> · {currentAnalysis.mover === 'w' ? (game.white?.name || 'White') : (game.black?.name || 'Black')}</span>
            </span>
          </div>
          <div style={styles.analysisReason}>{currentAnalysis.reason}</div>
          {currentAnalysis.best && currentAnalysis.cls.key !== 'best' && (
            <div style={styles.bestMoveRow}>
              <span style={styles.bestMoveLabel}>Move bora ingekuwa:</span>
              <span style={styles.bestMoveSan}>{currentAnalysis.best.san}</span>
              <span style={styles.bestMovePath}>({currentAnalysis.best.from} → {currentAnalysis.best.to})</span>
            </div>
          )}
        </div>
      ) : (
        <div style={styles.introCard}>
          <div style={styles.introText}>Uchambuzi wa move hii unakuja…</div>
        </div>
      )}

      {/* MOVE LIST — chips colored by how good each move was */}
      <div style={styles.moveListBox}>
        {history.length === 0 ? (
          <div style={styles.empty}>No moves recorded for this game.</div>
        ) : (
          <div style={styles.moveGrid}>
            {history.map((m, i) => {
              const a = analysis[i];
              return (
                <button
                  key={i}
                  style={{
                    ...styles.moveChip,
                    ...(a ? { borderColor: a.cls.border, color: a.cls.color } : {}),
                    ...(moveIndex === i + 1 ? styles.moveChipActive : {}),
                  }}
                  onClick={() => setMoveIndex(i + 1)}
                >
                  {i % 2 === 0 ? `${Math.floor(i / 2) + 1}. ` : ''}{m.san}{a && (a.cls.key === 'mistake' || a.cls.key === 'blunder' || a.cls.key === 'inaccuracy') ? a.cls.emoji : ''}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* END-OF-GAME SUMMARY — totals per player, once analysis finishes */}
      {done && (
        <div style={styles.summaryCard}>
          <div style={styles.summaryTitle}>Muhtasari wa Mchezo</div>
          <div style={styles.summaryGrid}>
            {[
              { name: game.white?.name || 'White', s: whiteSummary, glyph: '♔' },
              { name: game.black?.name || 'Black', s: blackSummary, glyph: '♚' },
            ].map((p, idx) => p.s && (
              <div key={idx} style={styles.summaryCol}>
                <div style={styles.summaryName}>{p.glyph} {p.name}</div>
                <div style={styles.summaryAccuracy}>{p.s.accuracy}%<span style={styles.summaryAccLabel}> usahihi</span></div>
                <div style={styles.summaryStats}>
                  <span style={{ color: CLASSES.best.color }}>★ {p.s.counts.best + p.s.counts.good} nzuri</span>
                  <span style={{ color: CLASSES.inaccuracy.color }}>?! {p.s.counts.inaccuracy} dosari</span>
                  <span style={{ color: CLASSES.mistake.color }}>? {p.s.counts.mistake} makosa</span>
                  <span style={{ color: CLASSES.blunder.color }}>?? {p.s.counts.blunder} makubwa</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {lesson && (
        <div style={styles.lessonCard}>
          <div style={styles.lessonBadge}>Somo la Maisha · {lesson.skill}</div>
          <div style={styles.lessonSwahili}>{lesson.swahili}</div>
          <div style={styles.lessonEnglish}>{lesson.english}</div>
        </div>
      )}

      <button style={styles.doneBtn} onClick={() => navigate('/chess')}>Rudi Lobby</button>
    </div>
  );
}

// Light "Sage & Cream" theme — matches the in-game screen.
const styles = {
  backIconBtn: { width: '42px', height: '42px', borderRadius: '13px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  saveIconBtn: { width: '42px', height: '42px', borderRadius: '13px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.35)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  saveIconBtnDone: { background: 'linear-gradient(135deg, #6EE7A8, #34D399)', border: 'none' },
  page: { minHeight: '100vh', background: 'radial-gradient(circle at 30% 0%, #262D2A 0%, #1B201E 48%, #121614 100%)', padding: '18px 14px 50px' },
  topBar: { maxWidth: '560px', margin: '0 auto 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' },
  backLink: { color: 'rgba(236,239,234,0.72)', fontSize: '13px', textDecoration: 'none', fontWeight: '600' },
  gameTitle: { color: '#ECEFEA', fontSize: '13px', fontWeight: '700' },
  controls: { maxWidth: '560px', margin: '14px auto', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' },
  ctrlBtn: { background: '#232927', border: '1px solid rgba(255,255,255,0.14)', color: '#D9F5E7', padding: '9px 14px', borderRadius: '10px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' },
  moveCounter: { color: 'rgba(236,239,234,0.58)', fontSize: '12px', minWidth: '60px', textAlign: 'center', fontWeight: '700' },
  analyzingBar: { maxWidth: '560px', margin: '0 auto 14px', background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px 14px', color: 'rgba(236,239,234,0.72)', fontSize: '12.5px', fontWeight: '600', textAlign: 'center' },
  progressTrack: { marginTop: '8px', height: '6px', borderRadius: '99px', background: '#2B322E', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '99px', background: 'linear-gradient(90deg, #10B981, #2E9E6B)', transition: 'width 0.15s ease' },
  introCard: { maxWidth: '560px', margin: '0 auto 14px', background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '16px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)' },
  introTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '16px', color: '#D9F5E7', fontWeight: '700', marginBottom: '6px' },
  introText: { color: 'rgba(236,239,234,0.72)', fontSize: '13px', lineHeight: 1.6 },
  analysisCard: { maxWidth: '560px', margin: '0 auto 14px', border: '1.5px solid', borderRadius: '14px', padding: '14px 16px' },
  analysisHead: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' },
  classBadge: { border: '1.5px solid', borderRadius: '99px', padding: '4px 12px', fontSize: '11.5px', fontWeight: '800', background: '#232927' },
  analysisMove: { color: '#ECEFEA', fontSize: '14.5px', fontWeight: '800', fontFamily: 'monospace' },
  moverTag: { color: 'rgba(236,239,234,0.58)', fontSize: '11.5px', fontWeight: '600', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },
  analysisReason: { color: 'rgba(236,239,234,0.88)', fontSize: '13px', lineHeight: 1.6 },
  bestMoveRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '10px', background: 'rgba(0,0,0,0.25)', border: '1px dashed rgba(255,255,255,0.22)', borderRadius: '10px', padding: '9px 12px' },
  bestMoveLabel: { color: 'rgba(236,239,234,0.72)', fontSize: '12px', fontWeight: '700' },
  bestMoveSan: { color: '#34D399', fontSize: '14px', fontWeight: '800', fontFamily: 'monospace' },
  bestMovePath: { color: 'rgba(236,239,234,0.58)', fontSize: '12px', fontFamily: 'monospace' },
  moveListBox: { maxWidth: '560px', margin: '0 auto 16px', background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '14px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)' },
  empty: { color: 'rgba(236,239,234,0.45)', fontSize: '12.5px', textAlign: 'center' },
  moveGrid: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  moveChip: { background: '#2B322E', border: '1.5px solid rgba(255,255,255,0.1)', color: 'rgba(236,239,234,0.72)', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '700' },
  moveChipActive: { background: '#D9F5E7', borderColor: '#D9F5E7', color: '#232927' },
  summaryCard: { maxWidth: '560px', margin: '0 auto 16px', background: '#232927', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '16px', boxShadow: '0 4px 14px rgba(0,0,0,0.28)' },
  summaryTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '16px', color: '#D9F5E7', fontWeight: '700', marginBottom: '12px', textAlign: 'center' },
  summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  summaryCol: { background: '#2B322E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '12px', textAlign: 'center' },
  summaryName: { color: '#ECEFEA', fontSize: '13px', fontWeight: '800', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  summaryAccuracy: { color: '#D9F5E7', fontFamily: "'Fraunces', Georgia, serif", fontSize: '24px', fontWeight: '800' },
  summaryAccLabel: { fontSize: '11px', color: 'rgba(236,239,234,0.58)', fontWeight: '600', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },
  summaryStats: { display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '8px', fontSize: '11.5px', fontWeight: '700' },
  lessonCard: { maxWidth: '560px', margin: '0 auto 16px', background: 'linear-gradient(135deg, rgba(52,211,153,0.12), rgba(52,211,153,0.05))', border: '1px solid rgba(52,211,153,0.4)', borderRadius: '14px', padding: '16px' },
  lessonBadge: { fontSize: '10.5px', fontWeight: '800', color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  lessonSwahili: { color: '#ECEFEA', fontSize: '14px', fontWeight: '700', marginBottom: '4px', lineHeight: 1.5 },
  lessonEnglish: { color: 'rgba(236,239,234,0.5)', fontSize: '12px', fontStyle: 'italic', lineHeight: 1.5 },
  doneBtn: { display: 'block', margin: '0 auto', background: 'linear-gradient(135deg, #D9F5E7, #10B981)', border: 'none', color: '#fff', padding: '12px 26px', borderRadius: '11px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 18px rgba(16,185,129,0.3)' },
  centerMsg: { color: '#ECEFEA', textAlign: 'center', padding: '80px 20px', fontSize: '14px' },
  link: { color: '#34D399', fontWeight: '700' },
};
